import React from "react";
import { Link } from "react-router-dom";
import {
  Cloud,
  Activity,
  ServerCog,
  Workflow,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { GlowOrb, Overline } from "../components/SectionDecor";

const Services = () => {
  const { t } = useLanguage();
  const items = [
    { key: "consulting", icon: Cloud },
    { key: "supervision", icon: Activity },
    { key: "infra", icon: ServerCog },
    { key: "tools", icon: Workflow },
  ];

  return (
    <div data-testid="page-services" className="relative">
      <GlowOrb color="#00d4ff" size={420} opacity={0.1} className="top-0 -left-32" />

      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 pb-16">
        <div className="max-w-3xl">
          <Overline>{t.services.overline}</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter">
            {t.services.title}
          </h1>
          <p className="mt-5 text-lg text-zinc-400">{t.services.subtitle}</p>
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 pb-28">
        <div className="space-y-8">
          {items.map(({ key, icon: Icon }, idx) => {
            const it = t.services.items[key];
            const reverse = idx % 2 === 1;
            return (
              <div
                key={key}
                data-testid={`services-item-${key}`}
                className="smk-card rounded-2xl p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start"
              >
                <div className={`lg:col-span-4 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="h-12 w-12 rounded-xl smk-gradient-bg flex items-center justify-center">
                    <Icon size={20} className="text-black" />
                  </div>
                  <p className="mt-5 font-mono text-xs uppercase tracking-widest text-[#00d4ff]">
                    0{idx + 1} / 04
                  </p>
                  <h2 className="mt-2 text-2xl lg:text-3xl font-semibold tracking-tight">
                    {it.title}
                  </h2>
                </div>
                <div className={`lg:col-span-8 ${reverse ? "lg:order-1" : ""}`}>
                  <p className="text-zinc-300 leading-relaxed">{it.desc}</p>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {it.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-sm text-zinc-300"
                      >
                        <CheckCircle2 size={16} className="mt-0.5 text-[#00ffb3] flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 flex flex-wrap gap-4">
          <Link
            to="/offers"
            data-testid="services-to-offers"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full smk-gradient-bg text-black font-semibold hover:brightness-110 transition"
          >
            {t.nav.offers}
            <ArrowRight size={16} />
          </Link>
          <Link
            to="/contact"
            data-testid="services-to-contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white hover:border-[#00d4ff]/50 hover:bg-white/5 transition"
          >
            {t.nav.cta}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
